export class SACBaseService {
    baseUrl;
    encoder = (input) => btoa(input);
    serializer;
    deserializer;
    async request(path, options) {
        if (!this.baseUrl)
            throw new Error("baseUrl is not defined");
        const url = `${this.baseUrl}${path}`;
        const response = await fetch(url, {
            ...options,
            body: JSON.stringify(options.body),
        });
        const data = await response.json();
        if (!response.ok)
            throw new Error(data?.message || `HTTP error! Status: ${response.status}`);
        return data;
    }
    async requestWithAuth(path, options) {
        const deserializedData = this.deserializer();
        if (!deserializedData ||
            !deserializedData.firstName ||
            !deserializedData.lastName ||
            !deserializedData.token) {
            throw new Error("Unauthenticated data");
        }
        const { token } = deserializedData;
        options.headers = {
            Authorization: token,
        };
        return this.request(path, options);
    }
    teacherInfo() {
        const deserializedData = this.deserializer();
        return {
            firstName: deserializedData.firstName,
            lastName: deserializedData.lastName,
            teacherId: deserializedData.teacherId,
        };
    }
    isAuthenticated() {
        const deserializedData = this.deserializer();
        return !!(deserializedData &&
            deserializedData.firstName &&
            deserializedData.lastName &&
            deserializedData.token &&
            deserializedData.teacherId);
    }
    async checkAuth() {
        const { teacherId } = this.teacherInfo();
        try {
            await this.getLecturesByTeacherId(teacherId, new Date(2024, 0, 1), new Date());
            return true;
        }
        catch (err) {
            console.error(err);
            // clear the current session
            this.serializer({
                firstName: undefined,
                lastName: undefined,
                teacherId: undefined,
                token: undefined,
            });
            return false;
        }
    }
    logout() {
        this.serializer({
            token: undefined,
            firstName: undefined,
            lastName: undefined,
            teacherId: undefined,
        });
    }
    async login(username, password) {
        if (!this.serializer)
            throw new Error("serializer not available");
        const body = {
            operation: "loginAuth",
            encodeUsername: this.encoder ? this.encoder(username) : username,
            encodePassword: this.encoder ? this.encoder(password) : password,
        };
        const data = await this.request("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1" && data.coachToken && data.coachToken !== "NULL") {
            const result = {
                token: data.coachToken,
                firstName: data.first_name,
                lastName: data.last_name,
                teacherId: data.coach_id,
            };
            this.serializer(result);
            return result;
        }
        else {
            throw new Error(data.message ?? "Login error");
        }
    }
    async getClassList(year, term) {
        const body = {
            operation: "loadClassList",
            year: year,
            term: term,
            coach_id: this.teacherInfo().teacherId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: false,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            return (data.classArray?.map((cls) => ({
                groupName: cls.group_name,
                schoolName: cls.school_name,
                schoolId: cls.school_id,
                classId: cls.cid,
            })) ?? []);
        }
        else {
            throw new Error("Failed to load class list");
        }
    }
    async getClassDetails(year, term, classId, classDateId) {
        const body = {
            operation: "loadStudentList",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            return {
                topic: data.topic,
                schoolName: data.schoolName,
                classStartDate: data.classStartDate,
                students: data.studentArray?.map((student) => {
                    return {
                        studentId: student.student_id,
                        firstName: student.first_name,
                        lastName: student.last_name,
                        score: Number(student.score),
                        attend: student.attend === "1",
                        behavior: Number(student.behavior) + 3,
                        nthPaymentRemainder: Number(student.nthPaymentRemainder),
                        termPaidStatus: student.termPaidStatus,
                        parentContactIsEmpty: student.parentContactIsEmpty,
                    };
                }) ?? [],
            };
        }
        else {
            throw new Error("Failed to load class details");
        }
    }
    async updateStudentList(year, term, classDateId, classId, topic, students) {
        const studentList = students.map((s) => ({
            studentid: s.studentId,
            score: s.score.toString(),
            attend: s.attend ? "1" : "0",
            behavior: (s.behavior - 3).toString(),
            awards: "",
        }));
        const body = {
            operation: "updateStudentList",
            year: year,
            term: term,
            class_id: classId,
            class_date_id: classDateId,
            topic,
            studentlist: studentList,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status !== "1") {
            throw new Error("Failed to update student list");
        }
    }
    async getLecturesByTeacherId(teacherId, fromDate, toDate) {
        const body = {
            operation: "returnListOfClassDatesByCoachIdandTimeFrame",
            coach_id: teacherId,
            fromDate: Math.floor(fromDate.getTime() / 1000),
            toDate: Math.floor(toDate.getTime() / 1000),
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            return data.result.map((r) => {
                return {
                    cdid: r.cdid,
                    classId: r.class_id,
                    year: r.year,
                    term: r.term,
                    day: r.day,
                    date: r.date,
                    startTime: r.start_time,
                    endTime: r.end_time,
                    coachId: r.coach_id,
                    coachReminded: r.coach_reminded,
                    rollSubmitted: r.roll_submitted,
                    rollApproved: r.roll_approved,
                    rollApprovedBy: r.roll_approved_by,
                    topicsCovered: r.topics_covered,
                    firstName: r.first_name,
                    lastName: r.last_name,
                };
            });
        }
        else {
            throw new Error("Failed to load lectures by coach");
        }
    }
    async getLecturesByClassId(classId) {
        const body = {
            operation: "returnListOfClassDatesByClassId",
            class_id: classId,
        };
        const data = await this.requestWithAuth("", {
            method: "POST",
            cors: true,
            headers: {
                "Content-Type": "application/json",
            },
            body: body,
        });
        if (data.status === "1") {
            return data.result.map((r) => {
                return {
                    cdid: r.cdid,
                    classId: r.class_id,
                    year: r.year,
                    term: r.term,
                    day: r.day,
                    date: r.date,
                    startTime: r.start_time,
                    endTime: r.end_time,
                    coachId: r.coach_id,
                    coachReminded: r.coach_reminded,
                    rollSubmitted: r.roll_submitted,
                    rollApproved: r.roll_approved,
                    rollApprovedBy: r.roll_approved_by,
                    topicsCovered: r.topics_covered,
                    firstName: r.first_name,
                    lastName: r.last_name,
                };
            });
        }
        else {
            throw new Error("Failed to load lectures by class id");
        }
    }
}
